import {
  type EventLocation,
  EVENT_LOCATIONS,
  findEventEntry,
} from "@repo/vihangam-yog-shared";
import { ChevronDown, Download, ScanLine } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";

import { downloadCsv } from "../components/DataKit";
import {
  groupEventsForPicker,
  isEventToday,
  type PickerGroups,
} from "../lib/eventTiming";
import { friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

import { AtAGlance } from "./event-registrations/AtAGlance";
import { KundMap } from "./event-registrations/KundMap";
import { ManualAddition } from "./event-registrations/ManualAddition";
import { MoreAboutEvent } from "./event-registrations/MoreAboutEvent";
import { RailItem } from "./event-registrations/RailItem";
import { RosterTable } from "./event-registrations/RosterTable";
import {
  eventMetaLine,
  eventWhenAndWhere,
} from "./event-registrations/guestFormat";
import {
  GUEST_LIST_HEADER,
  guestListRow,
} from "./event-registrations/guestListCsv";
import { type EventGroup, type RosterRow } from "./event-registrations/shared";

/** How often the page's idea of "now" moves on (Today, Finished, New). */
const CLOCK_TICK_MS = 60_000;

/** A city, or the bucket for events with no city. */
type LocationKey = EventLocation | "none";

/** One city's part of the event list. */
interface LocationSection {
  key: LocationKey;
  label: string;
  events: PickerGroups<EventGroup>;
}

/** Weekly online sessions and anything else without a city. */
const NO_CITY_LABEL = "Online and weekly";

const PRIMARY_BUTTON =
  "inline-flex min-h-11 items-center gap-2 rounded-md bg-[color:var(--color-amber)] px-5 text-[15px] font-semibold text-[color:var(--color-bg)] transition-opacity hover:opacity-90";

const SECONDARY_BUTTON =
  "inline-flex min-h-11 items-center gap-2 rounded-md border border-[color:var(--color-line-strong)] px-5 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]";

/**
 * Events: pick an event on the left (a picker on phones), and see it as
 * one page: when and where, a few numbers at a glance with the rest under
 * "More about this event", Manual addition for paid events, and the guest
 * list. "All events" is one guest list across every event. Polls every
 * 15 seconds.
 */
export function EventRegistrations() {
  const query = trpc.adminListEventRegistrations.useQuery(undefined, {
    retry: 2,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  const [selected, setSelected] = useState<string | null>(null);
  // Phones: the list folds behind a picker so the chosen event is at the
  // top of the screen. Wider screens show the list all the time.
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerListId = useId();
  // Hand-toggled city sections; absent means the section's default.
  const [expandOverrides, setExpandOverrides] = useState<
    Map<LocationKey, boolean>
  >(new Map());
  const [finishedOpen, setFinishedOpen] = useState<Set<LocationKey>>(new Set());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  const data = query.data;
  const clock = useMemo(() => new Date(now), [now]);

  // Sections by city in the shared display order, the no-city bucket
  // last, each split into Today / Coming up / Every week / Finished.
  const sections: LocationSection[] = useMemo(() => {
    if (!data) {
      return [];
    }
    const byKey = new Map<LocationKey, EventGroup[]>();
    for (const g of data.events) {
      const key: LocationKey = g.eventLocation ?? "none";
      byKey.set(key, [...(byKey.get(key) ?? []), g]);
    }
    const ordered: LocationSection[] = [];
    for (const location of EVENT_LOCATIONS) {
      const events = byKey.get(location.id);
      if (events) {
        ordered.push({
          key: location.id,
          label: location.label,
          events: groupEventsForPicker(events, clock),
        });
      }
    }
    const none = byKey.get("none");
    if (none) {
      ordered.push({
        key: "none",
        label: NO_CITY_LABEL,
        events: groupEventsForPicker(none, clock),
      });
    }
    return ordered;
  }, [data, clock]);

  // Open on today's event, else the next one coming up, else everyone.
  useEffect(() => {
    if (selected !== null || !data) {
      return;
    }
    const all = groupEventsForPicker(data.events, clock);
    setSelected(all.today[0]?.eventSlug ?? all.comingUp[0]?.eventSlug ?? "all");
  }, [data, selected, clock]);

  const group = useMemo(() => {
    if (!data || !selected || selected === "all") {
      return null;
    }
    return data.events.find((g) => g.eventSlug === selected) ?? null;
  }, [data, selected]);

  /** Rows in focus: one event's guests, or every event's. */
  const baseRows: RosterRow[] = useMemo(() => {
    if (!data) {
      return [];
    }
    return group
      ? group.registrations.map((r) => ({ ...r, eventTitle: group.eventTitle }))
      : data.events.flatMap((g) =>
          g.registrations.map((r) => ({ ...r, eventTitle: g.eventTitle })),
        );
  }, [data, group]);

  const isPaid = useMemo(
    () => baseRows.some((r) => r.bookingType != null),
    [baseRows],
  );

  // A city with nothing today, coming up or weekly opens folded; a hand
  // toggle always wins.
  function isExpanded(section: LocationSection): boolean {
    const override = expandOverrides.get(section.key);
    if (override !== undefined) {
      return override;
    }
    const { today, comingUp, everyWeek } = section.events;
    return today.length + comingUp.length + everyWeek.length > 0;
  }

  function selectEvent(slug: string) {
    setSelected(slug);
    setPickerOpen(false);
  }

  function toggleFinished(key: LocationKey) {
    setFinishedOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function openReception(slug: string) {
    window.history.pushState({}, "", `/reception/${encodeURIComponent(slug)}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  function downloadGuestList() {
    downloadCsv(
      group ? `${group.eventSlug}-registrations.csv` : "all-registrations.csv",
      GUEST_LIST_HEADER,
      baseRows.map(guestListRow),
    );
  }

  function railItem(g: EventGroup, muted: boolean) {
    return (
      <RailItem
        key={g.eventSlug}
        active={selected === g.eventSlug}
        onClick={() => selectEvent(g.eventSlug)}
        title={g.eventTitle}
        date={g.eventDateLabel}
        meta={eventMetaLine(g.totalPeople, g.registrationCount)}
        today={isEventToday(g, clock)}
        muted={muted}
      />
    );
  }

  // Singapore's events have no city, so its list is one section and
  // needs no headings.
  const showSectionHeadings = sections.length > 1;

  // The event list, shared by the wide-screen list and the phone picker.
  const railBody = data ? (
    <>
      <RailItem
        active={selected === "all"}
        onClick={() => selectEvent("all")}
        title="All events"
        meta={eventMetaLine(data.totalPeople, data.totalRegistrations)}
      />

      {sections.map((section) => {
        const expanded = !showSectionHeadings || isExpanded(section);
        const { today, comingUp, everyWeek, finished } = section.events;
        const count =
          today.length + comingUp.length + everyWeek.length + finished.length;
        const showFinished = finishedOpen.has(section.key);
        return (
          <div key={section.key} className="mt-5">
            {showSectionHeadings ? (
              <button
                type="button"
                onClick={() =>
                  setExpandOverrides((prev) =>
                    new Map(prev).set(section.key, !expanded),
                  )
                }
                aria-expanded={expanded}
                className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-1 text-left transition-colors hover:bg-[color:var(--color-paper)]/60"
              >
                <span className="text-[14px] font-semibold text-[color:var(--color-ink)]">
                  {section.label}
                  <span className="font-normal text-[color:var(--color-ink-soft)]">
                    {" "}
                    · {count} {count === 1 ? "event" : "events"}
                  </span>
                </span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-[color:var(--color-ink-soft)] transition-transform ${
                    expanded ? "" : "-rotate-90"
                  }`}
                  aria-hidden="true"
                />
              </button>
            ) : null}

            {expanded ? (
              <div className="mt-1">
                {today.map((g) => railItem(g, false))}
                {comingUp.length > 0 ? (
                  <>
                    <p className="mt-3 mb-1 pl-3.5 text-[14px] font-semibold text-[color:var(--color-ink-soft)]">
                      Coming up
                    </p>
                    {comingUp.map((g) => railItem(g, false))}
                  </>
                ) : null}
                {everyWeek.length > 0 ? (
                  <>
                    <p className="mt-3 mb-1 pl-3.5 text-[14px] font-semibold text-[color:var(--color-ink-soft)]">
                      Every week
                    </p>
                    {everyWeek.map((g) => railItem(g, false))}
                  </>
                ) : null}
                {finished.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleFinished(section.key)}
                      aria-expanded={showFinished}
                      className="mt-2 inline-flex min-h-11 items-center pl-3.5 text-[14px] font-medium text-[color:var(--color-ink-soft)] underline underline-offset-4 hover:text-[color:var(--color-ink)]"
                    >
                      {showFinished
                        ? "Hide finished events"
                        : `Show finished events (${finished.length})`}
                    </button>
                    {showFinished
                      ? finished.map((g) => railItem(g, true))
                      : null}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  ) : null;

  const price = group ? findEventEntry(group.eventSlug)?.price : undefined;
  const place = group ? eventWhenAndWhere(group) : null;
  const today = group ? isEventToday(group, clock) : false;

  return (
    <section className="mx-auto max-w-[1800px] px-5 py-8 md:px-10 md:py-12">
      <header className="border-b border-[color:var(--color-line)] pb-6">
        <h1 className="page-title">Events</h1>
        <p className="mt-2 max-w-prose text-[16px] text-[color:var(--color-ink-soft)]">
          Pick an event to see who is coming, add someone by hand, or download
          the guest list.
        </p>
      </header>

      {query.isLoading ? (
        <p className="mt-8 text-[16px] text-[color:var(--color-ink-soft)]">
          Loading events…
        </p>
      ) : null}

      {query.error && !data ? (
        <div role="alert" className="mt-8">
          <p className="text-[16px] text-[color:var(--color-ink)]">
            {friendlyError(
              query.error,
              "We couldn't load the events. Check your internet connection, then try again.",
            )}
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className={`mt-3 ${SECONDARY_BUTTON}`}
          >
            Try again
          </button>
        </div>
      ) : null}

      {query.error && data ? (
        <p className="mt-6 text-[14px] text-[color:var(--color-ink-soft)]">
          Couldn't refresh just now. The list below may be a minute old.
        </p>
      ) : null}

      {data && data.events.length === 0 ? (
        <p className="empty-state">No events to show yet.</p>
      ) : null}

      {data && data.events.length > 0 ? (
        <div className="mt-8 lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
          {/* Phones: a picker, so the chosen event sits at the top. */}
          <div className="mb-6 lg:hidden">
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              aria-expanded={pickerOpen}
              aria-controls={pickerListId}
              className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-4 py-3 text-left"
            >
              <span className="min-w-0">
                <span className="block text-[14px] font-medium text-[color:var(--color-ink-soft)]">
                  Event
                </span>
                <span className="mt-0.5 line-clamp-2 block text-[16px] font-semibold text-[color:var(--color-ink)]">
                  {group ? group.eventTitle : "All events"}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1 text-[15px] font-semibold text-[color:var(--color-amber)]">
                {pickerOpen ? "Close" : "Change"}
                <ChevronDown
                  size={18}
                  className={`transition-transform ${
                    pickerOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                />
              </span>
            </button>
            {pickerOpen ? (
              <nav
                id={pickerListId}
                aria-label="Choose an event"
                className="mt-2 max-h-[65vh] overflow-y-auto rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-paper)]/60 p-3"
              >
                {railBody}
              </nav>
            ) : null}
          </div>

          {/* Wider screens: the event list, sectioned by city. */}
          <nav
            aria-label="Event list"
            className="hidden lg:block lg:border-r lg:border-[color:var(--color-line)] lg:pr-6"
          >
            {railBody}
          </nav>

          {/* Keyed by event, so a search or a half-filled Manual addition
              never carries over to another event. */}
          <div key={group?.eventSlug ?? "all"} className="min-w-0">
            <div>
              {place ? (
                <>
                  {place.when ? (
                    <p className="text-[16px] text-[color:var(--color-ink-soft)]">
                      {place.when}
                    </p>
                  ) : null}
                  {place.where ? (
                    <p className="text-[16px] text-[color:var(--color-ink-soft)]">
                      {place.where}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="text-[16px] text-[color:var(--color-ink-soft)]">
                  Everyone who has signed up
                </p>
              )}
              <h2 className="mt-1 font-display text-heading text-[color:var(--color-ink)]">
                {group ? group.eventTitle : "All events"}
              </h2>
              <div className="mt-4 flex flex-wrap gap-3">
                {group ? (
                  <button
                    type="button"
                    onClick={() => openReception(group.eventSlug)}
                    className={today ? PRIMARY_BUTTON : SECONDARY_BUTTON}
                  >
                    <ScanLine size={18} aria-hidden="true" />
                    {today ? "Check people in" : "Open Reception"}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={downloadGuestList}
                  className={SECONDARY_BUTTON}
                >
                  <Download size={18} aria-hidden="true" />
                  {group ? "Download guest list" : "Download everyone"}
                </button>
              </div>
            </div>

            <AtAGlance
              group={group}
              allTotals={{
                bookings: data.totalRegistrations,
                people: data.totalPeople,
              }}
              now={now}
            />

            {group ? <MoreAboutEvent group={group} now={now} /> : null}

            {group?.eventSlug === "hawan-yajna-sep-19" ? (
              <KundMap group={group} />
            ) : null}

            {group && price ? (
              <ManualAddition eventSlug={group.eventSlug} price={price} />
            ) : null}

            <RosterTable
              rows={baseRows}
              showEventColumn={!group}
              isPaid={isPaid}
              eventIsToday={today}
              now={now}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
